import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/devLogger';
import {
  FamilyGroupWithMembers,
  FamilyMember,
  FamilyRole,
} from '../types';
import { requestDeduplicator } from '../lib/requestCache';
import { withTimeout, isTimeoutError } from '../lib/queryUtils';

interface FamilyGroupState {
  familyGroups: FamilyGroupWithMembers[];
  activeFamilyId: string | null;
  loading: boolean;

  fetchFamilyGroups: () => Promise<void>;
  createFamilyGroup: (name: string) => Promise<{ success: boolean; error?: string; familyGroupId?: string }>;
  updateFamilyGroup: (familyGroupId: string, name: string) => Promise<{ success: boolean; error?: string }>;
  deleteFamilyGroup: (familyGroupId: string) => Promise<{ success: boolean; error?: string }>;
  setActiveFamilyId: (familyGroupId: string | null) => void;
  getActiveFamily: () => FamilyGroupWithMembers | null;
  getUserOwnedFamilyCount: () => Promise<number>;

  fetchFamilyMembers: (familyGroupId: string) => Promise<FamilyMember[]>;
  removeMember: (familyGroupId: string, memberId: string) => Promise<{ success: boolean; error?: string }>;
  updateMemberRole: (familyGroupId: string, memberId: string, newRole: FamilyRole) => Promise<{ success: boolean; error?: string }>;
  leaveFamily: (familyGroupId: string, newOwnerId?: string) => Promise<{ success: boolean; error?: string }>;
}

export const useFamilyGroupStore = create<FamilyGroupState>((set, get) => ({
  familyGroups: [],
  activeFamilyId: null,
  loading: false,

  fetchFamilyGroups: async () => {
    set({ loading: true });
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        logger.debug('[FamilyGroupStore] No authenticated user found');
        set({ familyGroups: [], loading: false });
        return;
      }

      // Fetch user's memberships with family group details
      const { data: membershipData, error: membershipError } = await withTimeout(
        supabase
          .from('family_members')
          .select(`
            id,
            family_group_id,
            user_id,
            role,
            invited_by,
            joined_at,
            family_groups (
              id,
              name,
              created_by,
              created_at,
              settings
            )
          `)
          .eq('user_id', user.id),
        25000,
        'Loading family groups timed out'
      );

      if (membershipError) {
        logger.error('[FamilyGroupStore] Error fetching memberships:', membershipError);
        set({ familyGroups: [], loading: false });
        throw membershipError;
      }

      // Extract unique family group IDs
      const familyGroupIds = (membershipData || [])
        .map((m: any) => m.family_groups?.id)
        .filter(Boolean);

      if (familyGroupIds.length === 0) {
        set({ familyGroups: [], loading: false });
        return;
      }

      // Fetch ALL members for ALL family groups in a single query
      const { data: allMembers, error: membersError } = await withTimeout(
        supabase
          .from('family_members')
          .select(`
            id,
            family_group_id,
            user_id,
            role,
            invited_by,
            joined_at
          `)
          .in('family_group_id', familyGroupIds),
        25000,
        'Loading family members timed out'
      );

      if (membersError) {
        logger.error('[FamilyGroupStore] Error fetching all members:', membersError);
        set({ familyGroups: [], loading: false });
        throw membersError;
      }

      // Group members by family_group_id
      const membersByFamily = new Map<string, FamilyMember[]>();
      (allMembers || []).forEach((member: any) => {
        if (!membersByFamily.has(member.family_group_id)) {
          membersByFamily.set(member.family_group_id, []);
        }
        membersByFamily.get(member.family_group_id)!.push(member);
      });

      // Build family groups with their members
      const familyGroupsWithMembers: FamilyGroupWithMembers[] = [];

      for (const membership of membershipData || []) {
        const familyGroup = membership.family_groups as any;

        if (!familyGroup) continue;

        const members = membersByFamily.get(familyGroup.id) || [];

        familyGroupsWithMembers.push({
          id: familyGroup.id,
          name: familyGroup.name,
          created_by: familyGroup.created_by,
          created_at: familyGroup.created_at,
          settings: familyGroup.settings,
          members,
          member_count: members.length,
          user_role: membership.role as FamilyRole,
        });
      }

      set({ familyGroups: familyGroupsWithMembers, loading: false });

      // Log loaded groups for debugging
      console.log('[FamilyGroupStore] Loaded', familyGroupsWithMembers.length, 'family groups:',
        familyGroupsWithMembers.map(f => ({ id: f.id, name: f.name })));

      // CRÍTICO: Verificar y limpiar IDs obsoletos SIEMPRE (no solo si !activeFamilyId)
      if (familyGroupsWithMembers.length > 0) {
        const currentActiveFamilyId = get().activeFamilyId;
        const storedFamilyId = localStorage.getItem('activeFamilyId');

        // Check if we're in invitation flow (just accepted an invitation)
        const justAcceptedInvitation = sessionStorage.getItem('just_accepted_invitation') === 'true';
        const invitationFamilyId = sessionStorage.getItem('invitation_family_id');

        console.log('[FamilyGroupStore] Current active:', currentActiveFamilyId);
        console.log('[FamilyGroupStore] Stored in localStorage:', storedFamilyId);

        if (justAcceptedInvitation && invitationFamilyId) {
          console.log('[FamilyGroupStore] INVITATION FLOW DETECTED - Preserving family:', invitationFamilyId);

          // Verify the invitation family exists in loaded groups
          const invitationFamilyExists = familyGroupsWithMembers.some(f => f.id === invitationFamilyId);

          if (invitationFamilyExists) {
            // CRITICAL: Preserve the invitation family as active
            const family = familyGroupsWithMembers.find(f => f.id === invitationFamilyId);
            console.log('[FamilyGroupStore] Setting invitation family as active:', family?.name);

            set({ activeFamilyId: invitationFamilyId });
            localStorage.setItem('activeFamilyId', invitationFamilyId);

            // Don't clear the flags here - let Dashboard clear them after mounting
            return; // Exit early to prevent overriding this choice
          } else {
            console.warn('[FamilyGroupStore] Invitation family not found in loaded groups:', invitationFamilyId);
            // Clear the flags if family doesn't exist
            sessionStorage.removeItem('just_accepted_invitation');
            sessionStorage.removeItem('invitation_family_id');
          }
        }

        // Normal flow (not from invitation)
        // Verificar si los IDs guardados existen en los grupos cargados
        const storedFamilyExists = storedFamilyId ?
          familyGroupsWithMembers.some(f => f.id === storedFamilyId) : false;

        const currentFamilyExists = currentActiveFamilyId ?
          familyGroupsWithMembers.some(f => f.id === currentActiveFamilyId) : false;

        // Limpiar localStorage si el ID almacenado no existe
        if (storedFamilyId && !storedFamilyExists) {
          console.log('[FamilyGroupStore] Stored family ID not found in loaded groups, cleaning localStorage');
          localStorage.removeItem('activeFamilyId');
        }

        // Limpiar activeFamilyId del store si no existe
        if (currentActiveFamilyId && !currentFamilyExists) {
          console.log('[FamilyGroupStore] Current family ID not found in loaded groups, resetting');
          set({ activeFamilyId: null });
        }

        // Establecer familia activa
        const finalActiveFamilyId = get().activeFamilyId;

        if (!finalActiveFamilyId) {
          // No hay familia activa válida, establecer una
          const validStoredId = storedFamilyId && storedFamilyExists ? storedFamilyId : null;
          const familyToActivate = validStoredId || familyGroupsWithMembers[0].id;

          console.log('[FamilyGroupStore] Setting active family to:', familyToActivate,
            familyGroupsWithMembers.find(f => f.id === familyToActivate)?.name);

          set({ activeFamilyId: familyToActivate });
          localStorage.setItem('activeFamilyId', familyToActivate);
        } else {
          console.log('[FamilyGroupStore] Active family is valid:', finalActiveFamilyId,
            familyGroupsWithMembers.find(f => f.id === finalActiveFamilyId)?.name);
        }
      }
    } catch (error: any) {
      logger.error('[FamilyGroupStore] Error fetching family groups:', error);
      set({ familyGroups: [], loading: false });
    }
  },

  createFamilyGroup: async (name: string) => {
    try {
      // Use getSession to ensure we have a valid JWT token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        logger.error('[FamilyGroupStore] Session error:', sessionError);
        return { success: false, error: 'Error al verificar la sesión. Intenta refrescar la página.' };
      }

      if (!session?.user) {
        logger.error('[FamilyGroupStore] No active session found');
        return { success: false, error: 'No hay sesión activa. Por favor, inicia sesión nuevamente.' };
      }

      const user = session.user;
      logger.debug('[FamilyGroupStore] Creating family group for user:', user.id);

      logger.debug('[FamilyGroupStore] Calling RPC to create family group:', name.trim());
      const { data: familyGroup, error: createError } = await supabase
        .rpc('create_family_group_rpc', { p_name: name.trim() })
        .single();

      if (createError) {
        logger.error('[FamilyGroupStore] Error creating family group:', createError);

        if (createError.message?.includes('can only own up to 4')) {
          return {
            success: false,
            error: 'Solo puedes ser propietario de hasta 4 grupos familiares'
          };
        }

        if (createError.code === '42501') {
          return {
            success: false,
            error: 'Error de permisos. Por favor, cierra sesión y vuelve a iniciar sesión.'
          };
        }

        throw createError;
      }

      logger.debug('[FamilyGroupStore] Family group created via RPC:', familyGroup.id);

      requestDeduplicator.invalidateCache('fetchFamilyGroups');
      await get().fetchFamilyGroups();

      logger.debug('[FamilyGroupStore] Family group creation completed successfully');
      return { success: true, familyGroupId: familyGroup.id };
    } catch (error: any) {
      logger.error('[FamilyGroupStore] Error creating family group:', error);
      return {
        success: false,
        error: error.message || 'Error al crear el grupo familiar. Intenta nuevamente.'
      };
    }
  },

  updateFamilyGroup: async (familyGroupId: string, name: string) => {
    try {
      const { error } = await supabase
        .from('family_groups')
        .update({ name: name.trim() })
        .eq('id', familyGroupId);

      if (error) throw error;

      requestDeduplicator.invalidateCache('fetchFamilyGroups');
      set(state => ({
        familyGroups: state.familyGroups.map(fg =>
          fg.id === familyGroupId ? { ...fg, name: name.trim() } : fg
        ),
      }));

      return { success: true };
    } catch (error: any) {
      console.error('Error updating family group:', error);
      return { success: false, error: error.message || 'Failed to update family group' };
    }
  },

  deleteFamilyGroup: async (familyGroupId: string) => {
    try {
      const { error } = await supabase
        .from('family_groups')
        .delete()
        .eq('id', familyGroupId);

      if (error) throw error;

      requestDeduplicator.invalidateCache('fetchFamilyGroups');
      set(state => ({
        familyGroups: state.familyGroups.filter(fg => fg.id !== familyGroupId),
        activeFamilyId: state.activeFamilyId === familyGroupId ? null : state.activeFamilyId,
      }));

      return { success: true };
    } catch (error: any) {
      console.error('Error deleting family group:', error);
      return { success: false, error: error.message || 'Failed to delete family group' };
    }
  },

  setActiveFamilyId: (familyGroupId: string | null) => {
    set({ activeFamilyId: familyGroupId });

    if (familyGroupId) {
      localStorage.setItem('activeFamilyId', familyGroupId);
    } else {
      localStorage.removeItem('activeFamilyId');
    }
  },

  getActiveFamily: () => {
    const { familyGroups, activeFamilyId } = get();
    return familyGroups.find(fg => fg.id === activeFamilyId) || null;
  },

  getUserOwnedFamilyCount: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) return 0;

      const { data, error } = await supabase.rpc('get_user_owned_family_count', {
        user_id_param: user.id,
      });

      if (error) throw error;

      return data || 0;
    } catch (error) {
      console.error('Error getting owned family count:', error);
      return 0;
    }
  },

  fetchFamilyMembers: async (familyGroupId: string) => {
    try {
      console.log('[FamilyGroupStore] Fetching members for family:', familyGroupId);

      // Fetch family members
      const { data: membersData, error: membersError } = await withTimeout(
        supabase
          .from('family_members')
          .select('*')
          .eq('family_group_id', familyGroupId),
        15000,
        'Loading family members timed out'
      );

      if (membersError) {
        console.error('[FamilyGroupStore] Error fetching family members:', membersError);
        throw membersError;
      }

      if (!membersData || membersData.length === 0) {
        console.log('[FamilyGroupStore] No members found');
        return [];
      }

      console.log('[FamilyGroupStore] Found', membersData.length, 'members');

      // Fetch user profiles using secure RPC function
      console.log('[FamilyGroupStore] Fetching profiles via RPC function...');
      const { data: profilesData, error: profilesError } = await withTimeout(
        supabase.rpc('get_family_member_profiles', {
          p_family_group_id: familyGroupId
        }),
        15000,
        'Loading user profiles timed out'
      );

      if (profilesError) {
        console.error('[FamilyGroupStore] Error fetching profiles via RPC:', profilesError);
        // Don't throw - continue with partial data
      }

      console.log('[FamilyGroupStore] Fetched', profilesData?.length || 0, 'profiles via RPC');

      // Create a map of user profiles by user_id for quick lookup
      const profilesMap = new Map(
        (profilesData || []).map(profile => [profile.user_id, profile])
      );

      // Merge members with profiles
      const mappedData = membersData.map(member => {
        const profile = profilesMap.get(member.user_id);

        const mapped = {
          ...member,
          email: profile?.email || null,
          first_name: profile?.first_name || null,
          last_name: profile?.last_name || null,
          second_last_name: profile?.second_last_name || null,
          full_name: profile?.full_name || null,
        };

        console.log('[FamilyGroupStore] Mapped member:', {
          id: mapped.id,
          user_id: mapped.user_id,
          email: mapped.email,
          first_name: mapped.first_name,
          last_name: mapped.last_name,
          has_profile: !!profile,
        });

        return mapped;
      });

      console.log('[FamilyGroupStore] Successfully mapped', mappedData.length, 'members with profiles');
      return mappedData;
    } catch (error: any) {
      console.error('[FamilyGroupStore] Error in fetchFamilyMembers:', error);

      if (isTimeoutError(error)) {
        throw new Error('La carga de miembros tardó demasiado tiempo. Por favor, inténtalo de nuevo.');
      }

      throw error;
    }
  },

  removeMember: async (familyGroupId: string, memberId: string) => {
    try {
      const { error } = await supabase
        .from('family_members')
        .delete()
        .eq('id', memberId)
        .eq('family_group_id', familyGroupId);

      if (error) throw error;

      // Invalidate caches since membership changed
      requestDeduplicator.invalidateCachePattern(familyGroupId);

      set(state => ({
        familyGroups: state.familyGroups.map(fg =>
          fg.id === familyGroupId
            ? {
                ...fg,
                members: fg.members.filter(m => m.id !== memberId),
                member_count: fg.member_count - 1,
              }
            : fg
        ),
      }));

      return { success: true };
    } catch (error: any) {
      console.error('Error removing member:', error);
      return { success: false, error: error.message || 'Failed to remove member' };
    }
  },

  updateMemberRole: async (familyGroupId: string, memberId: string, newRole: FamilyRole) => {
    try {
      const { error } = await supabase
        .from('family_members')
        .update({ role: newRole })
        .eq('id', memberId)
        .eq('family_group_id', familyGroupId);

      if (error) throw error;

      set(state => ({
        familyGroups: state.familyGroups.map(fg =>
          fg.id === familyGroupId
            ? {
                ...fg,
                members: fg.members.map(m => (m.id === memberId ? { ...m, role: newRole } : m)),
              }
            : fg
        ),
      }));

      return { success: true };
    } catch (error: any) {
      console.error('Error updating member role:', error);
      return { success: false, error: error.message || 'Failed to update member role' };
    }
  },

  leaveFamily: async (familyGroupId: string, newOwnerId?: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      if (newOwnerId) {
        const updateResult = await get().updateMemberRole(familyGroupId, newOwnerId, 'owner');
        if (!updateResult.success) {
          return updateResult;
        }
      }

      const { data: membership } = await supabase
        .from('family_members')
        .select('id')
        .eq('family_group_id', familyGroupId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (!membership) {
        return { success: false, error: 'You are not a member of this family' };
      }

      const { error } = await supabase
        .from('family_members')
        .delete()
        .eq('id', membership.id);

      if (error) throw error;

      await get().fetchFamilyGroups();

      return { success: true };
    } catch (error: any) {
      console.error('Error leaving family:', error);
      return { success: false, error: error.message || 'Failed to leave family' };
    }
  },
}));

